import { inject, Injectable, WritableSignal } from '@angular/core';
import { finalize, Observable, of, switchMap, tap } from 'rxjs';
import { CampaignMeasure } from '../types/campaign.interface';
import { GraphicMeasure, GraphMeasure } from '../types/graph-data.interface';
import { GraphTypeId } from '../types/graph.interface';
import { CustomScaleConfig } from '../types/graphic-metadata.interface';
import { MeasureConfig } from '../types/measure-config.interface';
import { DateRangeParams } from './measure-data.service';
import { GraphicService } from './api/graphic.service';
import { ChartLegendManager } from './chart-legend.manager';
import { EnvelopeManager } from './envelope.manager';
import { GraphMeasureSyncService } from './graph-measure-sync.service';
import { GraphicMetadataService } from './graphic-metadata.service';
import { MeasureDataService } from './measure-data.service';

export interface GraphDataState {
  graphMeasures: WritableSignal<GraphMeasure[]>;
  graphicMeasures: WritableSignal<GraphicMeasure[]>;
  measureValues: WritableSignal<Map<number, CampaignMeasure[]>>;
  sortedMeasureValues: WritableSignal<Map<number, CampaignMeasure[]>>;
  measureConfigs: WritableSignal<Map<number, MeasureConfig>>;
  customScales: WritableSignal<Map<number, CustomScaleConfig>>;
  availableDateRange: WritableSignal<{ minDate: Date | null; maxDate: Date | null }>;
  isLoadingMeasures: WritableSignal<boolean>;
}

export interface LoadMeasuresResult {
  measures: GraphMeasure[];
  measureValues: Map<number, CampaignMeasure[]>;
}

@Injectable()
export class GraphDataOrchestratorService {
  private readonly graphApiService = inject(GraphicService);
  private readonly measureDataService = inject(MeasureDataService);
  private readonly measureSync = inject(GraphMeasureSyncService);
  private readonly metadataService = inject(GraphicMetadataService);

  private sortByTimestamp(values: CampaignMeasure[]): CampaignMeasure[] {
    return [...values].sort((a, b) => {
      const dateA = a.timestamp ? new Date(a.timestamp).getTime() : 0;
      const dateB = b.timestamp ? new Date(b.timestamp).getTime() : 0;
      return dateA - dateB;
    });
  }

  private isValidId(id: number | null | undefined): boolean {
    return id !== null && id !== undefined && Number.isInteger(id) && id > 0 && id < 2147483647;
  }

  applyMeasuresData(
    measures: GraphMeasure[],
    measureValues: Map<number, CampaignMeasure[]>,
    state: GraphDataState,
    legendMgr: ChartLegendManager,
    envelopeMgr: EnvelopeManager,
    graphType: GraphTypeId,
    isRefresh = false,
  ): void {
    const sortedMap = new Map<number, CampaignMeasure[]>();

    measures.forEach(measure => {
      const values = measureValues.get(measure.id) ?? [];
      const { min, max } = this.measureSync.calculateMinMaxForMeasure(values);
      measure.minTimestamp = min ?? undefined;
      measure.maxTimestamp = max ?? undefined;

      const sorted = this.sortByTimestamp(values);
      sortedMap.set(measure.id, sorted);
    });

    const graphicMeasures = state.graphicMeasures();
    if (graphicMeasures.length > 0) {
      const deserializedMetadata = this.metadataService.deserializeFromMetadata(graphicMeasures, graphType, envelopeMgr.envelopes());

      if (isRefresh) {
        if (legendMgr.traceOrder().length === 0 && deserializedMetadata.order.length > 0) {
          legendMgr.traceOrder.set(deserializedMetadata.order);
        }
        if (legendMgr.traceVisibility().length === 0 && deserializedMetadata.visibility.length > 0) {
          legendMgr.traceVisibility.set(deserializedMetadata.visibility);
        }
      } else {
        legendMgr.traceStyles.set(deserializedMetadata.styles);
        state.customScales.set(deserializedMetadata.customScales);
        state.measureConfigs.set(deserializedMetadata.measureConfigs);

        if (deserializedMetadata.order.length > 0) {
          legendMgr.traceOrder.set(deserializedMetadata.order);
        }
        if (deserializedMetadata.visibility.length > 0) {
          legendMgr.traceVisibility.set(deserializedMetadata.visibility);
        }
      }
    }

    // New graph without persisted order: seed from the loaded measures so the
    // legend has a real order to reorder. Without this, moveTrace mutates an empty
    // array and applyStylesToTraces falls back to natural order, reverting the drag.
    if (legendMgr.traceOrder().length === 0 && measures.length > 0) {
      const seedIds = measures.map(measure => measure.id);
      legendMgr.traceOrder.set(seedIds);
      if (legendMgr.traceVisibility().length === 0) {
        legendMgr.traceVisibility.set(seedIds.map(() => true));
      }
    }

    state.graphMeasures.set(measures);
    state.measureValues.set(measureValues);
    state.sortedMeasureValues.set(sortedMap);

    const { min, max } = this.measureSync.calculateMinMaxForMeasure(Array.from(measureValues.values()).flat());
    state.availableDateRange.set({ minDate: min, maxDate: max });
  }

  loadPreconfiguredMeasures$(
    graphId: number,
    assetId: number,
    state: GraphDataState,
    dateRange?: DateRangeParams | DateRangeParams[],
    isRefresh = false,
    maxPoints?: number,
  ): Observable<LoadMeasuresResult | null> {
    if (!this.isValidId(graphId) || !this.isValidId(assetId)) {
      return of(null);
    }

    state.isLoadingMeasures.set(true);

    const currentMeasures = state.graphMeasures();
    if (isRefresh && currentMeasures.length > 0) {
      return this.measureDataService
        .getMeasuresWithValues(currentMeasures, dateRange, maxPoints)
        .pipe(finalize(() => state.isLoadingMeasures.set(false)));
    }

    const cachedGraphicMeasures = state.graphicMeasures();
    const graphicMeasures$ =
      cachedGraphicMeasures.length > 0
        ? of(cachedGraphicMeasures)
        : this.graphApiService.getGraphicMeasures(graphId).pipe(tap(measures => state.graphicMeasures.set(measures)));

    return graphicMeasures$.pipe(
      switchMap(preconfiguredMeasures => {
        if (preconfiguredMeasures.length === 0) {
          return of(null);
        }

        const measureIds = preconfiguredMeasures.map((gm: GraphicMeasure) => gm.measureId);

        return this.measureDataService.getMeasuresByIds(assetId, measureIds).pipe(
          switchMap(enrichedMeasures => {
            const selected = enrichedMeasures.filter(m => m.selected);
            return this.measureDataService.getMeasuresWithValues(selected, dateRange, maxPoints);
          }),
        );
      }),
      finalize(() => state.isLoadingMeasures.set(false)),
    );
  }
}
